import { getSlideDetailStore, TOTAL_DETAIL } from '@/common/stores/slide-detail';
import { DetailTypeToPermissionKey } from '@/common/stores/slide-detail.logic';
import { useTargetPermissionCheck } from '@/common/permission/permission.utils';
import { TargetStatusRequestTargetKind } from '@/api-client/target/model';
import { useMessage } from '@/common/inject/useMessage';
import { useInternational } from '@/common/locale';
import type {
  SlideDetailAdapterResult,
  SlideDetailAdapterResultKey,
  SlideDetailAdapterParams,
} from '@/common/types/slideDetailAdapter.types';
import { createSlideDetailResult } from '@/common/utils/slideDetailAdapterResult';
import type { RolePermissionKeyType } from '@/config/views/permission/rolePermission/rolePermission.type';
import { useInfraAdapterBase } from '@/infrastructure/adapter/infraDetail.shared';
import { getRequiredMap } from '@/common/utils/core/detailFieldMeta.utils';
import { z } from 'zod';

const networkObjectDetailInputSchema = z
  .object({
    networkObjectId: z.string().optional(),
    networkObjectName: z.string().optional(),
    targetId: z.string().optional(),
    targetName: z.string().optional(),
    selectedTab: z.string().optional(),
    fromTime: z.string().optional(),
    toTime: z.string().optional(),
    rawData: z.record(z.any()).optional(),
  })
  .transform(({ networkObjectId, targetId, networkObjectName, targetName, rawData, ...rest }) => ({
    ...rest,
    networkObjectId:
      networkObjectId || targetId || rawData?.networkObjectId || rawData?.targetId || '',
    networkObjectName:
      networkObjectName || targetName || rawData?.networkObjectName || rawData?.targetName || '',
    fromTime: rest.fromTime ?? rawData?.fromTime,
    toTime: rest.toTime ?? rawData?.toTime,
  }))
  .pipe(
    z.object({
      networkObjectId: z.string().min(1),
      networkObjectName: z.string().optional(),
      selectedTab: z.string().optional(),
      fromTime: z.string().optional(),
      toTime: z.string().optional(),
    }),
  );

export type NetworkObjectDetailInput = z.input<typeof networkObjectDetailInputSchema>;

export const networkObjectDetailRequiredMap = getRequiredMap(networkObjectDetailInputSchema);

const getMissingParams = (error: z.ZodError) =>
  error.issues
    .map((issue) => issue.path[0])
    .filter((path): path is string => typeof path === 'string')
    .join(', ');

export const useNetworkObjectDetailAdapter = (rolePermissionKey?: RolePermissionKeyType) => {
  const { isSlideDetailOpened, addTabPanel } = useInfraAdapterBase();
  const { checkIsValidTarget } = useTargetPermissionCheck();
  const { showErrorMsg } = useMessage();
  const { t } = useInternational();
  const { isDetailSlidePermissionDenied } = getSlideDetailStore();

  const validateNetworkObjectDetail = async (
    params: Partial<NetworkObjectDetailInput>,
    options: {
      showErrorMessage: boolean;
      checkPermission?: boolean;
      checkParams?: boolean;
      checkTarget?: boolean;
    },
  ): Promise<{
    reason: SlideDetailAdapterResultKey;
    parsedParams?: z.infer<typeof networkObjectDetailInputSchema>;
  }> => {
    const { checkPermission = true, checkParams = true, checkTarget = true } = options;

    // 1. 권한 체크
    if (checkPermission) {
      const denied = isDetailSlidePermissionDenied({
        rolePermissionKey:
          rolePermissionKey || DetailTypeToPermissionKey[TOTAL_DETAIL.NETWORK_OBJECT],
        type: TOTAL_DETAIL.NETWORK_OBJECT,
        showError: options.showErrorMessage,
      });
      if (denied) return { reason: 'permission_denied' };
    }

    // 2. 대상 정보 조회 및 필수값 검증
    const parsed = networkObjectDetailInputSchema.safeParse(params);
    if (checkParams && !parsed.success) {
      if (options.showErrorMessage) {
        showErrorMsg({
          msgStr: t('MESSAGE.LINK_MISSING_REQUIRED_PARAMS', {
            value: getMissingParams(parsed.error),
          }),
        });
      }
      return { reason: 'invalid_params' };
    }
    if (!parsed.success) {
      return { reason: 'invalid_params' };
    }
    const validResult = parsed.data;

    // 3. 타겟 유효성 검증
    if (checkTarget) {
      const targetValidation = await checkIsValidTarget(
        {
          targetId: validResult.networkObjectId,
          targetKind: TargetStatusRequestTargetKind.NETWORK_OBJECT,
        },
        {
          showMessageIfTargetNil: options.showErrorMessage,
          showErrorMessage: options.showErrorMessage,
        },
      );
      if (!targetValidation.valid) return { reason: targetValidation.reason };
    }

    return { reason: 'ok', parsedParams: validResult };
  };

  const openNetworkObjectDetail = async ({
    params,
    options,
    currentState,
    init,
  }: SlideDetailAdapterParams<
    Partial<NetworkObjectDetailInput>
  >): Promise<SlideDetailAdapterResult> => {
    const {
      runCheckPermission = true,
      runTargetValidation = true,
      showErrorMessage = true,
    } = options ?? {};

    const { reason, parsedParams } = await validateNetworkObjectDetail(params, {
      showErrorMessage,
      checkPermission: runCheckPermission,
      checkTarget: runTargetValidation,
    });
    if (reason !== 'ok' || !parsedParams) return createSlideDetailResult(reason);
    const validResult = parsedParams;

    const tab = {
      type: TOTAL_DETAIL.NETWORK_OBJECT,
      name: validResult.networkObjectName || '',
      init,
      addition: {
        networkObjectId: validResult.networkObjectId,
        fromTime: validResult.fromTime,
        toTime: validResult.toTime,
      },
      state: {
        selectedTab: validResult.selectedTab,
      },
    } as const;

    // 4. 탭 패널 열기
    const result = addTabPanel(tab, currentState);
    isSlideDetailOpened.value = getSlideDetailStore().isOpened;
    return createSlideDetailResult(result);
  };

  return { isSlideDetailOpened, openNetworkObjectDetail };
};
